/** Minimum amount a participant must have to initiate a cashout. */
export const MIN_CASHOUT_AMOUNT = 5;

/**
 * Minimum amount for a Whop cashout. Whop won't let an expert withdraw a payout
 * balance below this to their bank, so moving less would strand the funds in
 * Whop. We enforce it on the move so experts can always withdraw what they move.
 */
export const WHOP_MIN_CASHOUT_AMOUNT = 12.5;

/** The most one Virtual Incentives cash out can move. */
export const VI_MAX_CASHOUT_AMOUNT = 2000;

/**
 * Platform-wide circuit breaker on self-serve extraction: the most money every
 * expert combined may move out of their Terac balances in one UTC day, net of
 * failed-transfer reversals. Not a budget: a breaker. Tripping it fails every
 * cashout on every rail until a human raises it, so it is sized to be
 * unreachable in normal operation rather than to shape behavior.
 *
 * $250,000 is 1.25x the highest day on record (2026-10-10, $199,997), which is
 * the day that reached the previous $200,000: 163 experts cashed out and the
 * breaker then held 16 more. That day opened with a $189,765 operator batch to
 * 87 experts on a single study, a weekly batch that has grown every week since
 * 2026-09-12. Unlike every earlier figure, this one does not clear the entire
 * withdrawable balance outstanding: that batch alone took it to $363,134, so
 * "every expert cashes out everything today" can reach it again.
 *
 * The ceiling this has to stay under is the Whop payout float, which is the
 * platform ledger account's `usd` balance. Do NOT size against
 * `GET /accounts/{biz}`: it does not track the float. It read $348,779 on
 * 2026-09-19, the figure this cap was sized against, while spendable cash that
 * day was $0 -- so the "under two days" headroom was never there. Neither
 * $200,000 nor $250,000 was derived against the float: that is still an open
 * risk decision, not a correction.
 *
 * Operator payout batches (recordManualPayout / recordExternalPayout) do not
 * pass through the withdrawal path and are deliberately not counted here. They
 * are what fills the balances that get extracted a day or two later, so a batch
 * larger than the headroom left in this cap is the advance warning that it is
 * about to bind.
 *
 * There is no warning threshold here on purpose. The ledger emits the day's
 * running total as `extraction.daily_total` and Datadog decides what is
 * alarming, so the "getting close" line can be retuned without a deploy. Only
 * the refusal itself, which does need a code review to move, lives in code.
 */
export const DAILY_EXTRACTION_CAP = 250_000;

/**
 * Per-expert version of the same breaker, and the one that actually stops a
 * single compromised or exploiting account: the platform cap above is wide
 * enough that one account draining alone would never reach it.
 *
 * $10,000 went uncrossed by any expert on any day for all of Terac's history
 * before it shipped (the maximum was $9,700 on 2026-06-22, p99 $2,928). One
 * expert has since been held exactly at it: 2026-09-07, five withdrawals
 * against a $150/hour engagement. A large hourly payout is now a way to reach
 * this honestly, so a single hit is a support question, not yet a signal.
 *
 * There is no separate per-cashout maximum on the Whop rail, so this is also
 * the most one cashout can move. Virtual Incentives orders stay bounded by
 * `VI_MAX_CASHOUT_AMOUNT`.
 */
export const DAILY_EXTRACTION_CAP_PER_USER = 10_000;

/** Cashout minimum on the Whop rail, pre-formatted for display (e.g. "$12.50"). Virtual Incentives has its own, lower floor: `VI_MIN_CASHOUT_DISPLAY`. */
export const MIN_CASHOUT_DISPLAY = `$${Math.max(MIN_CASHOUT_AMOUNT, WHOP_MIN_CASHOUT_AMOUNT).toFixed(2)}`;

export const formatCashoutAmount = (amount: number) =>
  `$${amount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Per-expert daily ceiling, pre-formatted for display (e.g. "$10,000.00").
 * Expert-facing docs quote this rather than typing the figure, so a change to
 * the limit reaches the prose.
 */
export const DAILY_CASHOUT_LIMIT_DISPLAY = formatCashoutAmount(
  DAILY_EXTRACTION_CAP_PER_USER,
);

/**
 * One Virtual Incentives cash out's minimum and maximum, pre-formatted for the
 * expert docs, which quote them for the countries Whop cannot pay.
 */
export const VI_MIN_CASHOUT_DISPLAY = formatCashoutAmount(MIN_CASHOUT_AMOUNT);
export const VI_MAX_CASHOUT_DISPLAY = formatCashoutAmount(
  VI_MAX_CASHOUT_AMOUNT,
);

/**
 * When the per-expert daily limit next resets: the coming midnight UTC, the
 * boundary of the day the ledger sums extraction over.
 */
export function nextDailyCashoutReset(now: Date = new Date()): Date {
  return new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1),
  );
}

/**
 * Round a study incentive up to the nearest whole dollar. Keeps experts from
 * ever seeing a value below what was computed and avoids scammy-looking
 * fractional amounts like $43.02. Applied at the source where incentives are
 * persisted so the charge, payout, listings, and emails share one clean number.
 */
export function roundIncentiveUp(amount: number): number {
  return Math.ceil(amount);
}

/** Round a dollar amount to the nearest cent. */
export function roundToCents(amount: number): number {
  return Math.round(amount * 100) / 100;
}

/**
 * Canonical whole-dollar display for study incentives/payouts. Every surface
 * (emails, listings, apply flow, internal pages) formats the incentive through
 * this helper so they all render the same number. Floors rather than rounds so
 * the displayed figure is never greater than the stored payout: for incentives
 * created after they round up at the source this is exact, and for any legacy
 * fractional value it under-promises rather than over-promises.
 */
export function formatIncentive(amount: string | number): string {
  const num = typeof amount === "string" ? Number(amount) : amount;
  if (!Number.isFinite(num)) {
    return "";
  }
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Math.floor(num));
}

/**
 * A study's per-participant price: estimated price, else base payment, else 0.
 * Coerced before `||`: numeric columns arrive as strings, and "0.00" is truthy.
 */
export function resolveStudyIncentive(
  study:
    | {
        estimatedPricePerInterview: string | number | null;
        basePaymentAmount: string | number | null;
      }
    | null
    | undefined,
): number {
  return (
    Number(study?.estimatedPricePerInterview) ||
    Number(study?.basePaymentAmount) ||
    0
  );
}

/**
 * No price, so joiners take the study's: they are stamped its price when they
 * apply, and their rate rows copy its template, or open at its price when it
 * has none. Only the paid-out counter reads it: PAID whenever the study pays.
 */
export function unpricedLinkPaymentType(
  study: Parameters<typeof resolveStudyIncentive>[0],
): "PAID" | "UNPAID" {
  return resolveStudyIncentive(study) > 0 ? "PAID" : "UNPAID";
}

/**
 * Standard USD currency display with 2 decimals. Returns a hyphen for an
 * absent value (null, undefined, or empty string) so callers can render a
 * placeholder without their own null guard.
 */
export function formatUsdAmount(
  value: string | number | null | undefined,
): string {
  if (value === null || value === undefined || value === "") {
    return "-";
  }
  const num = typeof value === "string" ? Number(value) : value;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(num);
}

/**
 * Signed USD display for balances and ledger amounts, where a negative value is
 * a normal state rather than an error: an internal study wallet runs deeply
 * negative by design, and a reversal is rendered by negating the amount
 * (`formatSignedUsdAmount(-clawbackAmount)`) rather than by the caller
 * prefixing its own minus, which would double up on an already-negative input.
 *
 * `-0` collapses to `$0.00`. Intl renders it as `-$0.00`, which reads as a debt
 * on a zeroed balance.
 */
export function formatSignedUsdAmount(value: number): string {
  return formatUsdAmount(value === 0 ? 0 : value);
}

/**
 * The zone every expert-facing payout date is computed and rendered in, so a
 * cadence like "every Friday" names the same day for everyone.
 *
 * Deliberately separate from `OFFICE_HOURS_ZONE`
 * (`apps/web/src/lib/office-hours.ts`) even though both are currently ET: that
 * one means staff availability, this one is the finance calendar, and they are
 * free to diverge. It also lives here because the date maths runs in
 * `packages/api`, which cannot reach `apps/web`.
 */
export const TERAC_PAYOUT_TIMEZONE = "America/New_York";
